// Turns what a camera reads off a pack into a check query: a GTIN from a
// retail barcode or GS1 element string, or the URL in a QR code. Shared by
// the phone scan page (bundled into public/js/scan.js) and the tests.

export type ScanQuery = { ok: true; q: string } | { ok: false; reason: string };

/** UPC-E (8 digits, number system 0 or 1) to its 12-digit UPC-A. */
export function expandUpcE(upcE: string): string | undefined {
  if (!/^[01]\d{7}$/.test(upcE)) return undefined;
  const ns = upcE[0]!;
  const d = upcE.slice(1, 7);
  const check = upcE[7]!;
  const last = d[5]!;
  let body: string;
  if ("012".includes(last)) body = `${d.slice(0, 2)}${last}0000${d.slice(2, 5)}`;
  else if (last === "3") body = `${d.slice(0, 3)}00000${d.slice(3, 5)}`;
  else if (last === "4") body = `${d.slice(0, 4)}00000${d[4]}`;
  else body = `${d.slice(0, 5)}0000${last}`;
  return `${ns}${body}${check}`;
}

/**
 * @param raw the decoded text
 * @param format the barcode format as BarcodeDetector names it ("ean_13", "upc_e", "qr_code", …)
 */
export function queryFromScan(raw: string, format?: string): ScanQuery {
  const text = raw.trim();
  if (/^https?:\/\//i.test(text)) return { ok: true, q: text };

  if (format === "upc_e" && /^\d{8}$/.test(text)) {
    const upcA = expandUpcE(text);
    return upcA ? { ok: true, q: upcA } : { ok: false, reason: "That UPC-E code couldn't be expanded to a GTIN." };
  }
  if (/^\d{8}$|^\d{12,14}$/.test(text)) return { ok: true, q: text };

  // GS1 element strings (GS1 DataMatrix, GS1 QR): optional symbology identifier, FNC1 as GS (\x1d).
  const elements = text.replace(/^\][A-Za-z]\d/, "").replace(/\x1d/g, "");
  const plainAi = /^01(\d{14})/.exec(elements);
  if (plainAi) return { ok: true, q: plainAi[1]! };
  const bracketed = /\(01\)(\d{14})/.exec(elements);
  if (bracketed) return { ok: true, q: bracketed[1]! };

  return { ok: false, reason: "That code doesn't carry a GTIN or a web address." };
}
